import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import "../pages/ZReport.css";
import { toast } from "react-toastify";
import { DataGrid } from "@mui/x-data-grid";
// --- CSV Export Helper ---
function exportToCSV(rows, filename = "suppliers.csv") {
  if (!rows.length) return;
  const header = Object.keys(rows[0]);
  const csv =
    header.join(",") +
    "\n" +
    rows
      .map((row) =>
        header
          .map((field) =>
            String(row[field])
              .replace(/"/g, '""')
              .replace(/\n/g, " ")
          )
          .map((v) => `"${v}"`)
          .join(",")
      )
      .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

export default function SupplierModule({ onSave }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [supplierName, setSupplierName] = useState("");
  const [vatType, setVatType] = useState("20%");
  const [totalAmount, setTotalAmount] = useState("");
  const [customTaxable, setCustomTaxable] = useState("");
  const [customExempt, setCustomExempt] = useState("");
  const [customVat, setCustomVat] = useState("");
  const [method, setMethod] = useState("Cash");
  const [notes, setNotes] = useState(""); // NEW
  const [suppliers, setSuppliers] = useState([]);
  const [editIndex, setEditIndex] = useState(-1);

  // --- Filter/Search State ---
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [searchName, setSearchName] = useState("");

  // Fetch suppliers from DB on mount and after add/edit/delete
  const fetchSuppliers = async () => {
    const { data, error } = await supabase
      .from("suppliers")
      .select("*")
      .order("date", { ascending: true });
    if (error) {
      toast.error("❌ Error loading suppliers: " + error.message);
    } else if (data) {
      setSuppliers(
        data.map((s) => ({
          id: s.id, // NEW (use id for updates/deletes)
          date: s.date,
          supplierName: s.supplier_name,
          vatType: s.vat_type,
          totalAmount: Number(s.total_amount),
          netAmount: Number(s.net_amount),
          vatAmount: Number(s.vat_amount),
          exemptAmount: Number(s.exempt_amount),
          method: s.method,
          notes: s.notes || "", // NEW
          added_by: s.added_by,
          added_at: s.added_at,
          modified_by: s.modified_by,
          modified_at: s.modified_at,
        }))
      );
    }
  };

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line
  }, []);

  // 20% VAT calculations
  let net20 = 0, vat20 = 0;
  if (vatType === "20%" && totalAmount && !isNaN(totalAmount)) {
    net20 = parseFloat(totalAmount) / 1.2;
    vat20 = parseFloat(totalAmount) - net20;
  }

  // Custom VAT calculations
  const customTaxableNum = parseFloat(customTaxable) || 0;
  const customExemptNum = parseFloat(customExempt) || 0;
  const customVatNum = parseFloat(customVat) || 0;
  const customTotal = customTaxableNum + customExemptNum + customVatNum;
  const customTotalInput = parseFloat(totalAmount) || 0;
  const calcCustomVat = +(customTaxableNum * 0.2).toFixed(2);
  const calcCustomTotal = +(customTaxableNum + customExemptNum + customVatNum).toFixed(2);

  // Allow a little more tolerance for VAT rounding (e.g. 3p)
  const customVatMatch = Math.abs(calcCustomVat - customVatNum) < 0.03;
  const customTotalMatch = Math.abs(customTotal - customTotalInput) < 0.01;

  // Helper: Suggest if exempt amount looks like it should be taxable
  const exemptLooksTaxable =
    customExemptNum > 0 &&
    Math.abs(customVatNum - ((customTaxableNum + customExemptNum) * 0.2)) < 0.03;

  // --- Filtered and Searched Suppliers ---
  const filteredSuppliers = suppliers.filter((s) => {
    let dateMatch = true;
    if (filterFromDate && filterToDate) {
      dateMatch = s.date >= filterFromDate && s.date <= filterToDate;
    } else if (filterFromDate) {
      dateMatch = s.date >= filterFromDate;
    } else if (filterToDate) {
      dateMatch = s.date <= filterToDate;
    }
    const nameMatch = searchName
      ? s.supplierName.toLowerCase().includes(searchName.toLowerCase())
      : true;
    return dateMatch && nameMatch;
  });

  // --- Totals ---
  const totals = filteredSuppliers.reduce(
    (acc, s) => {
      acc.net += s.netAmount || 0;
      acc.vat += s.vatAmount || 0;
      acc.exempt += s.exemptAmount || 0;
      acc.total += s.totalAmount || 0;
      return acc;
    },
    { net: 0, vat: 0, exempt: 0, total: 0 }
  );

  // --- Payment Method Totals (for filtered suppliers) ---
  const paymentTotals = filteredSuppliers.reduce(
    (acc, s) => {
      const methodKey = s.method || "Other";
      if (!acc[methodKey]) acc[methodKey] = { net: 0, vat: 0, exempt: 0, total: 0 };
      acc[methodKey].net += s.netAmount || 0;
      acc[methodKey].vat += s.vatAmount || 0;
      acc[methodKey].exempt += s.exemptAmount || 0;
      acc[methodKey].total += s.totalAmount || 0;
      return acc;
    },
    {}
  );

  // Helper for rounding to 2 decimals
  const round2 = (n) => Number(Number(n).toFixed(2));

  // Add or update supplier (save to DB immediately)
  const handleAddSupplier = async () => {
    if (!supplierName.trim()) {
      toast.error("Supplier name should not be empty.");
      return;
    }

    // --- Extra validation: no negatives ---
    const nonNeg = (v) => (v === "" || isNaN(v)) ? true : Number(v) >= 0;

    let entry = {};
    if (vatType === "20%") {
      if (!totalAmount || isNaN(totalAmount)) return;
      if (!nonNeg(totalAmount)) {
        toast.error("❌ Amounts cannot be negative.");
        return;
      }
      entry = {
        date,
        supplierName,
        vatType,
        totalAmount: round2(totalAmount),
        netAmount: round2(net20),
        vatAmount: round2(vat20),
        exemptAmount: 0,
        method,
        notes: notes || "",
      };
    } else if (vatType === "No VAT") {
      if (!totalAmount || isNaN(totalAmount)) return;
      if (!nonNeg(totalAmount)) {
        toast.error("❌ Amounts cannot be negative.");
        return;
      }
      entry = {
        date,
        supplierName,
        vatType,
        totalAmount: round2(totalAmount),
        netAmount: round2(totalAmount),
        vatAmount: 0,
        exemptAmount: 0,
        method,
        notes: notes || "",
      };
    } else if (vatType === "Custom") {
      if (
        !customTaxable ||
        !customVat ||
        !customExempt ||
        !totalAmount ||
        isNaN(customTaxableNum) ||
        isNaN(customVatNum) ||
        isNaN(customExemptNum) ||
        isNaN(customTotalInput)
      ) return;

      if (!(nonNeg(customTaxable) && nonNeg(customVat) && nonNeg(customExempt) && nonNeg(totalAmount))) {
        toast.error("❌ Amounts cannot be negative.");
        return;
      }

      if (!customVatMatch) {
        if (Math.abs(calcCustomVat - customVatNum) < 0.05) {
          if (!window.confirm(
            `Entered VAT differs from 20% of taxable by more than 2p. Are you sure you want to continue?`
          )) return;
        } else {
          toast.error("Entered VAT does not match 20% of taxable amount.");
          return;
        }
      }
      if (!customTotalMatch) {
        toast.error(
          "Sum of Taxable + Exempt + VAT must equal Total Amount. Please check your entries."
        );
        return;
      }
      entry = {
        date,
        supplierName,
        vatType,
        totalAmount: round2(customTotalInput),
        netAmount: round2(customTaxableNum),
        vatAmount: round2(customVatNum),
        exemptAmount: round2(customExemptNum),
        method,
        notes: notes || "",
      };
    }

    try {
      const { data: auth } = await supabase.auth.getUser();
      const user =
        auth?.user?.user_metadata?.display_name ||
        auth?.user?.email ||
        "Unknown";

      if (editIndex >= 0) {
        // Update in DB using ID (safer)
        const old = suppliers[editIndex];
        const { error } = await supabase
          .from("suppliers")
          .update({
            date: entry.date,
            supplier_name: entry.supplierName,
            vat_type: entry.vatType,
            total_amount: entry.totalAmount,
            net_amount: entry.netAmount,
            vat_amount: entry.vatAmount,
            exempt_amount: entry.exemptAmount,
            method: entry.method,
            notes: entry.notes,
            modified_by: user,
            modified_at: new Date().toISOString(),
          })
          .eq("id", old.id);
        if (error) {
          toast.error("❌ Error updating supplier: " + error.message);
          return;
        }
        await fetchSuppliers();
        setEditIndex(-1);
        toast.success("Supplier updated successfully!");
      } else {
        // Insert in DB with audit fields
        const { error } = await supabase.from("suppliers").insert([{
          date: entry.date,
          supplier_name: entry.supplierName,
          vat_type: entry.vatType,
          total_amount: entry.totalAmount,
          net_amount: entry.netAmount,
          vat_amount: entry.vatAmount,
          exempt_amount: entry.exemptAmount,
          method: entry.method,
          notes: entry.notes,
          added_by: user,
          added_at: new Date().toISOString(),
        }]);
        if (error) {
          toast.error("❌ Error saving supplier: " + error.message);
          return;
        }
        await fetchSuppliers();
        toast.success("Supplier added successfully!");
      }
    } catch (e) {
      toast.error("❌ Error: " + e.message);
      return;
    }

    setSupplierName("");
    setTotalAmount("");
    setVatType("20%");
    setCustomTaxable("");
    setCustomExempt("");
    setCustomVat("");
    setMethod("Cash");
    setNotes(""); // reset notes
    if (onSave) onSave();
  };

  // Edit supplier
  const handleEdit = (idx) => {
    const s = suppliers[idx];
    setSupplierName(s.supplierName);
    setVatType(s.vatType);
    setTotalAmount(s.totalAmount);
    setCustomTaxable(s.netAmount);
    setCustomVat(s.vatAmount);
    setCustomExempt(s.exemptAmount);
    setMethod(s.method);
    setNotes(s.notes || "");
    setEditIndex(idx);
  };

  // Delete supplier (from DB and local)
  const handleDelete = async (idx) => {
    const supplier = suppliers[idx];
    const confirm = window.confirm(`Delete ${supplier.supplierName} entry on ${supplier.date}?`);
    if (!confirm) return;

    const { error } = await supabase
      .from("suppliers")
      .delete()
      .eq("id", supplier.id);
    if (error) {
      toast.error("❌ Error deleting supplier: " + error.message);
      return;
    }
    await fetchSuppliers();
    if (editIndex === idx) setEditIndex(-1);
    toast.success("Supplier deleted successfully!");
  };
  const columns = [
  { field: "date", headerName: "Date", width: 120 },
  { field: "supplierName", headerName: "Name", width: 160 },
  { field: "vatType", headerName: "VAT Type", width: 120 },
  {
    field: "netAmount", headerName: "Net (£)", width: 120,
    renderCell: (p) => <span style={{ color: "#d32f2f" }}>£{p.value.toFixed(2)}</span>
  },
  {
    field: "vatAmount", headerName: "VAT (£)", width: 120,
    renderCell: (p) => <span style={{ color: "#d32f2f" }}>£{p.value.toFixed(2)}</span>
  },
  {
    field: "exemptAmount", headerName: "Exempt (£)", width: 120,
    renderCell: (p) => <span style={{ color: "#d32f2f" }}>£{p.value.toFixed(2)}</span>
  },
  {
    field: "totalAmount", headerName: "Total (£)", width: 140,
    renderCell: (p) => <span style={{ color: "#d32f2f" }}>£{p.value.toFixed(2)}</span>
  },
  { field: "method", headerName: "Method", width: 100 },
  { field: "notes", headerName: "Notes", width: 180 },
  { field: "added_by", headerName: "Added By", width: 140 },
  {
    field: "added_at", headerName: "Added At", width: 170,
    renderCell: (p) => p.value ? new Date(p.value).toLocaleString() : ""
  },
  { field: "modified_by", headerName: "Modified By", width: 140 },
  {
    field: "modified_at", headerName: "Modified At", width: 170,
    renderCell: (p) => p.value ? new Date(p.value).toLocaleString() : ""
  },
  {
    field: "actions", headerName: "⚙️ Actions", width: 160, sortable: false,
    renderCell: (p) => (
      <div style={{ display: "flex", gap: 6 }}>
        <button className="update" onClick={() => handleEdit(suppliers.findIndex(s => s.id === p.row.id))}>
          Edit
        </button>
        <button className="btn-delete" onClick={() => handleDelete(suppliers.findIndex(s => s.id === p.row.id))}>
          Delete
        </button>
      </div>
    )
  }
];


  return (
    <div className="zreport-container">
      <h2>Supplier Module</h2>
      <div className="form-section">
        <div className="section-title">Add Supplier</div>
        <div className="supplier-form-row">
          <div className="supplier-form-col">
            <label>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="supplier-form-col">
            <label>Supplier Name</label>
            <input
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
            />
          </div>
          <div className="supplier-form-col">
            <label>VAT Type</label>
            <select
              value={vatType}
              onChange={(e) => setVatType(e.target.value)}
            >
              <option value="20%">20%</option>
              <option value="No VAT">No VAT</option>
              <option value="Custom">Custom</option>
            </select>
          </div>

          {/* 20% VAT */}
          {vatType === "20%" && (
            <div className="supplier-form-col">
              <label>Total Amount (£)</label>
              <input
                type="number"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                style={{
                  border: "2px solid #d32f2f",
                  background: "#fff8f8",
                }}
              />
              {totalAmount && !isNaN(totalAmount) && (
                <div className="supplier-summary">
                  Net: <span style={{ color: "#d32f2f" }}>£{net20.toFixed(2)}</span> + VAT:{" "}
                  <span style={{ color: "#d32f2f" }}>£{vat20.toFixed(2)}</span> ={" "}
                  <b style={{ color: "#d32f2f" }}>£{parseFloat(totalAmount).toFixed(2)}</b>
                </div>
              )}
            </div>
          )}

          {/* No VAT */}
          {vatType === "No VAT" && (
            <div className="supplier-form-col">
              <label>Total Amount (£)</label>
              <input
                type="number"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                style={{
                  border: "2px solid #d32f2f",
                  background: "#fff8f8",
                }}
              />
              {totalAmount && !isNaN(totalAmount) && (
                <div className="supplier-summary">
                  Net: <span style={{ color: "#d32f2f" }}>£{parseFloat(totalAmount).toFixed(2)}</span> (No VAT)
                </div>
              )}
            </div>
          )}

          {/* Custom VAT */}
          {vatType === "Custom" && (
            <>
              <div className="supplier-form-col">
                <label>Taxable Amount (£)</label>
                <input
                  type="number"
                  value={customTaxable}
                  onChange={(e) => setCustomTaxable(e.target.value)}
                />
              </div>
              <div className="supplier-form-col">
                <label>Exempt Amount (£)</label>
                <input
                  type="number"
                  value={customExempt}
                  onChange={(e) => setCustomExempt(e.target.value)}
                />
              </div>
              <div className="supplier-form-col">
                <label>VAT Amount (£)</label>
                <input
                  type="number"
                  value={customVat}
                  onChange={(e) => setCustomVat(e.target.value)}
                  style={{
                    border: "2px solid #d32f2f",
                    background: "#fff8f8",
                  }}
                />
              </div>
              <div className="supplier-form-col">
                <label>Total Amount (£)</label>
                <input
                  type="number"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  style={{
                    border: "2px solid #d32f2f",
                    background: "#fff8f8",
                  }}
                />
                {(customTaxable || customVat || customExempt || totalAmount) && (
                  <div className="supplier-summary">
                    <span>
                      <b>System VAT (20%):</b> £{calcCustomVat.toFixed(2)}{" "}
                      {customVat && !customVatMatch && (
                        <span style={{ color: "#d32f2f" }}>
                          ❌ Entered VAT does not match 20% of taxable
                        </span>
                      )}
                    </span>
                    <br />
                    <span>
                      <b>System Total:</b> £{calcCustomTotal.toFixed(2)}{" "}
                      {totalAmount && !customTotalMatch && (
                        <span style={{ color: "#d32f2f" }}>
                          ❌ Entered Total does not match sum
                        </span>
                      )}
                    </span>
                    <br />
                    {/* Suggest if exempt amount looks like it should be taxable */}
                    {exemptLooksTaxable && !customVatMatch && (
                      <div style={{ color: "#d32f2f", marginTop: 8 }}>
                        <b>How to fix:</b> If VAT is being charged on both Taxable and Exempt amounts, add them together:<br />
                        <b>Taxable Amount should be:</b> £{(customTaxableNum + customExemptNum).toFixed(2)}<br />
                        <b>Exempt Amount should be:</b> £0.00<br />
                        <span style={{ color: "#1976d2" }}>
                          Enter <b>£{(customTaxableNum + customExemptNum).toFixed(2)}</b> in Taxable Amount and <b>£0.00</b> in Exempt Amount.<br />
                          VAT will then be £{((customTaxableNum + customExemptNum) * 0.2).toFixed(2)} and Total will be £{(customTaxableNum + customExemptNum + ((customTaxableNum + customExemptNum) * 0.2)).toFixed(2)}.
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="supplier-form-col">
            <label>Payment Method</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              <option value="Cash">Cash</option>
              <option value="Card">Card</option>
              <option value="BACS">BACS</option>
            </select>
          </div>

          {/* Notes (neatly aligned, small textarea) */}
          <div className="supplier-form-col" style={{ flex: 1 }}>
            <label>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              style={{ width: "100%" }}
              placeholder="Optional notes..."
            />
          </div>

          <div
            className="supplier-form-col"
            style={{ alignItems: "flex-end", justifyContent: "flex-end" }}
          >
            <button
              className={editIndex >= 0 ? "update" : ""}
              style={{ marginTop: 22, minWidth: 120 }}
              onClick={handleAddSupplier}
              type="button"
            >
              {editIndex >= 0 ? "Update Supplier" : "+ Add Supplier"}
            </button>
          </div>
        </div>
      </div>

      {/* --- Filter/Search Controls --- */}
      <div style={{ margin: "16px 0", display: "flex", gap: 16, alignItems: "center" }}>
        <div>
          <label style={{ fontWeight: 500 }}>From: </label>
          <input
            type="date"
            value={filterFromDate}
            onChange={e => setFilterFromDate(e.target.value)}
          />
        </div>
        <div>
          <label style={{ fontWeight: 500 }}>To: </label>
          <input
            type="date"
            value={filterToDate}
            onChange={e => setFilterToDate(e.target.value)}
          />
        </div>
        <div>
          <label style={{ fontWeight: 500 }}>Search Name: </label>
          <input
            type="text"
            placeholder="Supplier Name"
            value={searchName}
            onChange={e => setSearchName(e.target.value)}
          />
        </div>
        <button
          style={{ marginLeft: 8 }}
          onClick={() => exportToCSV(filteredSuppliers.map(s => ({
            Date: s.date,
            Name: s.supplierName,
            "VAT Type": s.vatType,
            Net: s.netAmount?.toFixed(2),
            VAT: s.vatAmount?.toFixed(2),
            Exempt: s.exemptAmount?.toFixed(2),
            Total: s.totalAmount?.toFixed(2),
            Method: s.method,
            Notes: s.notes || ""
          })))}
          disabled={filteredSuppliers.length === 0}
        >
          Export to CSV
        </button>
      </div>

      {/* Supplier Table */}
     <div style={{ marginTop: 24 }}>
  <h3>📜 Suppliers</h3>
  <DataGrid
    rows={filteredSuppliers}
    columns={columns}
    getRowId={(row) => row.id}
    pageSize={10}
    rowsPerPageOptions={[5, 10, 25, 50]}
    autoHeight
    disableSelectionOnClick
    components={{
      Footer: () => (
        <div style={{
          padding: "10px",
          fontWeight: "bold",
          background: "#f5f5f5",
          borderTop: "1px solid #ddd"
        }}>
          Totals → Net: £{totals.net.toFixed(2)} | VAT: £{totals.vat.toFixed(2)} | Exempt: £{totals.exempt.toFixed(2)} | Total: £{totals.total.toFixed(2)}
        </div>
      )
    }}
    sx={{
    border: "1px solid #ccc",
    "& .MuiDataGrid-cell": {
      borderRight: "1px solid #ddd !important",
    },
    "& .MuiDataGrid-columnSeparator": {
      display: "block !important",
    },
    "& .MuiDataGrid-row": {
      borderBottom: "1px solid #eee !important",
    },
  }}
  />
</div>


      {/* --- Summary by Payment Method Section --- */}
      <section style={{ marginTop: 32, padding: 20, background: "#f9f9f9", borderRadius: 8 }}>
        <h3 style={{ marginBottom: 12 }}>Summary by Payment Method</h3>
        <table className="parsed-table" style={{ width: "auto", marginBottom: 8 }}>
          <thead>
            <tr>
              <th>Method</th>
              <th>Net</th>
              <th>VAT</th>
              <th>Exempt</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(paymentTotals).map(([method, vals]) => (
              <tr key={method}>
                <td>{method}</td>
                <td style={{ color: "#d32f2f" }}>£{vals.net.toFixed(2)}</td>
                <td style={{ color: "#d32f2f" }}>£{vals.vat.toFixed(2)}</td>
                <td style={{ color: "#d32f2f" }}>£{vals.exempt.toFixed(2)}</td>
                <td style={{ color: "#d32f2f" }}>£{vals.total.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontWeight: 500, marginTop: 8 }}>
          <span style={{ marginRight: 24 }}>Grand Net: <span style={{ color: "#d32f2f" }}>£{totals.net.toFixed(2)}</span></span>
          <span style={{ marginRight: 24 }}>Grand VAT: <span style={{ color: "#d32f2f" }}>£{totals.vat.toFixed(2)}</span></span>
          <span style={{ marginRight: 24 }}>Grand Exempt: <span style={{ color: "#d32f2f" }}>£{totals.exempt.toFixed(2)}</span></span>
          <span>Grand Total: <span style={{ color: "#d32f2f" }}>£{totals.total.toFixed(2)}</span></span>
        </div>
      </section>
    </div>
  );
}
